import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Main } from "@/components/Main";
import { PageHeader } from "@/components/PageHeader";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { postClockIn, getTodayStatus } from "@/Api/timeEntries";
import { TimeEntryTypeLabel } from "@/types/timeEntry";
import { AlertTriangle, Camera, Check, Fingerprint, RefreshCw } from "lucide-react";

type Coords = { latitude: number; longitude: number };

export const ClockInPage = ({
  onSuccess,
  blockReason,
}: {
  onSuccess?: () => void;
  blockReason?: "entrada" | "overtime";
} = {}) => {
  const { toast } = useToastNavigate();

  // Tipo é sempre calculado pelo backend (ciclo fixo Entrada -> Início de
  // Intervalo -> Fim de Intervalo -> Saída) a partir da última batida do dia
  // do colaborador — não há seleção manual.
  const [nextType, setNextType] = useState<number | null>(null);
  const [coords, setCoords] = useState<Coords | null>(null);
  const [geoError, setGeoError] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const [photoBlob, setPhotoBlob] = useState<Blob | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [submitState, setSubmitState] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [successEntry, setSuccessEntry] = useState<any>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const requestGeolocation = () => {
    setGeoError(false);
    if (!("geolocation" in navigator)) {
      setGeoError(true);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        });
      },
      () => setGeoError(true),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const requestCamera = async () => {
    setCameraError(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      setCameraError(true);
    }
  };

  useEffect(() => {
    requestGeolocation();
    requestCamera();

    (async () => {
      const { data } = await getTodayStatus();
      setNextType(data?.response?.next_type ?? null);
    })();

    return () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    // Espelha a captura pra ficar igual ao preview (efeito selfie)
    ctx?.translate(canvas.width, 0);
    ctx?.scale(-1, 1);
    ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) return;
      setPhotoBlob(blob);
      setPhotoPreview(URL.createObjectURL(blob));
    }, "image/jpeg");
  };

  // Somente dev: gera uma imagem fake para testar sem câmera
  const simulatePhoto = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = 320;
    canvas.height = 320;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.fillStyle = "#888";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    canvas.toBlob((blob) => {
      if (!blob) return;
      setPhotoBlob(blob);
      setPhotoPreview(URL.createObjectURL(blob));
    }, "image/jpeg");
  };

  const retakePhoto = () => {
    setPhotoBlob(null);
    setPhotoPreview(null);
  };

  const canSubmit =
    !!coords && !!photoBlob && nextType !== null && submitState !== "submitting";

  const handleSubmit = async () => {
    if (!coords || !photoBlob) return;

    setSubmitState("submitting");

    const formData = new FormData();
    formData.append("latitude", String(coords.latitude));
    formData.append("longitude", String(coords.longitude));
    formData.append("photo", photoBlob, "batida.jpg");
    formData.append("device", "web");

    try {
      const { data, ok } = await postClockIn(formData);
      if (!ok) {
        throw new Error("Erro ao registrar a batida");
      }

      setSuccessEntry(data?.response);
      setSubmitState("success");
      toast({
        description: "Ponto registrado com sucesso!",
        action: <Check color="#1edc2b" />,
      });
      onSuccess?.();
    } catch (error) {
      setSubmitState("error");
      toast({
        description: "Erro ao registrar a batida",
        variant: "destructive",
      });
    }
  };

  if (submitState === "success" && successEntry) {
    return (
      <Main>
        <PageHeader icon={Fingerprint} title="Ponto registrado" />
        <div className="flex flex-col items-center gap-4 max-w-md mx-auto py-8">
          {photoPreview && (
            <img
              src={photoPreview}
              alt="Foto da batida"
              className="size-32 rounded-full object-cover border"
            />
          )}
          <div className="text-center">
            <div className="font-semibold text-lg">
              {TimeEntryTypeLabel[successEntry?.time_entry_type_id]}
            </div>
            <div className="text-muted-foreground">
              {successEntry?.clocked_at
                ? new Date(successEntry.clocked_at).toLocaleString("pt-BR")
                : new Date().toLocaleString("pt-BR")}
            </div>
          </div>

          {(successEntry?.is_late || successEntry?.within_geofence === false) && (
            <div className="flex flex-col gap-1 w-full rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              {successEntry?.is_late && (
                <div className="flex items-center gap-2">
                  <AlertTriangle className="size-4 shrink-0" />
                  Você bateu o ponto com atraso.
                </div>
              )}
              {successEntry?.within_geofence === false && (
                <div className="flex items-center gap-2">
                  <AlertTriangle className="size-4 shrink-0" />
                  Você está longe da sua unidade de trabalho.
                </div>
              )}
            </div>
          )}
        </div>
      </Main>
    );
  }

  return (
    <Main>
      <PageHeader icon={Fingerprint} title="Bater Ponto" />

      <div className="flex flex-col gap-4 max-w-md mx-auto py-4 w-full">
        {blockReason && (
          <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertTriangle className="size-4 shrink-0" />
            {blockReason === "entrada"
              ? "Para liberar o acesso ao sistema, é necessário bater o ponto de Entrada."
              : "Você atingiu o limite diário de hora extra. Bata a Saída para continuar."}
          </div>
        )}
        <div className="rounded-md border p-4 bg-zinc-50 dark:bg-[#2c2c2c] text-center">
          <div className="text-xs text-muted-foreground">Tipo da batida</div>
          <div className="text-lg font-semibold">
            {nextType !== null ? TimeEntryTypeLabel[nextType] : "Calculando..."}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">Selfie (obrigatória)</label>
          <div className="relative aspect-square w-full max-w-xs mx-auto overflow-hidden rounded-full border bg-black">
            {photoPreview ? (
              <img
                src={photoPreview}
                alt="Foto capturada"
                className="w-full h-full object-cover"
              />
            ) : (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover -scale-x-100"
              />
            )}
          </div>
          <canvas ref={canvasRef} className="hidden" />

          {cameraError && (
            <div className="text-center text-sm text-destructive">
              Não foi possível acessar a câmera. A foto é obrigatória para
              bater o ponto.
              <div className="mt-2">
                <Button type="button" variant="outline" onClick={requestCamera}>
                  <RefreshCw className="size-4 mr-1" /> Tentar novamente
                </Button>
              </div>
            </div>
          )}

          {!cameraError && !photoBlob && (
            <Button type="button" onClick={capturePhoto} className={cn(colors.bg_primary)}>
              <Camera className="size-4 mr-1" /> Capturar
            </Button>
          )}

          {import.meta.env.DEV && !photoBlob && (
            <Button type="button" variant="outline" onClick={simulatePhoto}>
              Simular foto (dev)
            </Button>
          )}

          {photoBlob && (
            <Button type="button" variant="outline" onClick={retakePhoto}>
              <RefreshCw className="size-4 mr-1" /> Tirar outra foto
            </Button>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">
            Localização (obrigatória)
          </label>
          {coords ? (
            <span className="text-sm text-muted-foreground">
              Localização capturada ({coords.latitude.toFixed(5)},{" "}
              {coords.longitude.toFixed(5)})
            </span>
          ) : geoError ? (
            <div className="text-sm text-destructive">
              Não foi possível obter sua localização. Ela é obrigatória para
              bater o ponto.
              <div className="mt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={requestGeolocation}
                >
                  <RefreshCw className="size-4 mr-1" /> Tentar novamente
                </Button>
              </div>
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">
              Obtendo localização...
            </span>
          )}
        </div>

        <Button
          type="button"
          className={cn(colors.bg_primary)}
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {submitState === "submitting" ? (
            <>
              Registrando <LoaderSpin />
            </>
          ) : (
            "Confirmar Batida"
          )}
        </Button>
      </div>
    </Main>
  );
};

export default ClockInPage;
